import { getPenInfo, updatePenInfo } from '@/api/userServices'
import {
  DEFAULT_INSULIN_AGE_MODE,
  getInsulinActionConfig
} from '@/utils/loopInsulinModel'

export const PEN_RECORDS_UPDATED_EVENT = 'tr-pen-info-updated'
export const INSULIN_CONFIG_BASELINE_TIME = '1970-01-01 00:00:01'

let saveQueue = Promise.resolve()

function emitPenRecordsUpdated(uid, records, penType) {
  if (typeof window === 'undefined') return
  const detail = {
    uid,
    penType,
    insulinRecordList: records,
    loading: false,
    source: 'jilu'
  }
  let event
  if (typeof window.CustomEvent === 'function') {
    event = new window.CustomEvent(PEN_RECORDS_UPDATED_EVENT, { detail })
  } else {
    event = document.createEvent('CustomEvent')
    event.initCustomEvent(PEN_RECORDS_UPDATED_EVENT, false, false, detail)
  }
  window.dispatchEvent(event)
}

export function parsePenRecords(value) {
  if (Array.isArray(value)) return value
  try {
    const records = value ? JSON.parse(value) : []
    return Array.isArray(records) ? records : []
  } catch (error) {
    return []
  }
}

export function parsePenRecordTime(time) {
  const value = new Date(String(time || '').replace(/-/g, '/')).getTime()
  return Number.isNaN(value) ? 0 : value
}

export function parseInsulinConfigHistory(value) {
  if (Array.isArray(value)) return value.filter(item => item && typeof item === 'object')
  try {
    const history = value ? JSON.parse(value) : []
    return Array.isArray(history) ? history.filter(item => item && typeof item === 'object') : []
  } catch (error) {
    return []
  }
}

export function createInsulinConfigSnapshot({
  insulinType,
  ageMode = DEFAULT_INSULIN_AGE_MODE,
  activeTime,
  peakTime,
  delayTime,
  customTime = false
} = {}) {
  const actionConfig = getInsulinActionConfig(insulinType, ageMode, {
    insulinType,
    ageMode,
    activeTime,
    peakTime,
    delayTime,
    customTime
  })
  return {
    insulinType: actionConfig.insulinType,
    ageMode: actionConfig.ageMode,
    activeTime: actionConfig.effectDurationMinutes,
    peakTime: actionConfig.peakAfterInjectionMinutes,
    delayTime: actionConfig.delayMinutes,
    customTime: Boolean(customTime)
  }
}

export function getLatestInsulinConfig(history) {
  return parseInsulinConfigHistory(history).reduce((latest, config) => {
    if (!hasInsulinConfigSnapshot(config)) return latest
    const configTime = parsePenRecordTime(config.createTime || config.time)
    const latestTime = parsePenRecordTime(latest && (latest.createTime || latest.time))
    return configTime >= latestTime ? config : latest
  }, null)
}

export function isSameInsulinConfig(first, second) {
  if (!first || !second) return false
  return (
    String(first.insulinType) === String(second.insulinType) &&
    first.ageMode === second.ageMode &&
    Number(first.activeTime) === Number(second.activeTime) &&
    Number(first.peakTime) === Number(second.peakTime) &&
    Number(first.delayTime) === Number(second.delayTime) &&
    Boolean(first.customTime) === Boolean(second.customTime)
  )
}

export function appendInsulinConfig(history, config, createTime) {
  const parsedHistory = parseInsulinConfigHistory(history)
  if (!config || isSameInsulinConfig(getLatestInsulinConfig(parsedHistory), config)) return parsedHistory
  return [
    ...parsedHistory,
    {
      ...config,
      createTime
    }
  ]
}

function parsePenSetting(value) {
  if (value && typeof value === 'object') return value
  try {
    const setting = value ? JSON.parse(value) : {}
    return setting && typeof setting === 'object' ? setting : {}
  } catch (error) {
    return {}
  }
}

export function getPenInfoConfigHistory(penInfo = {}) {
  const history = parseInsulinConfigHistory(penInfo.insulinConfigHistory)
  if (history.length) {
    const earliestConfig = history.reduce((earliest, config) => {
      const configTime = parsePenRecordTime(config.createTime || config.time)
      const earliestTime = parsePenRecordTime(earliest && (earliest.createTime || earliest.time))
      if (!configTime) return earliest
      return !earliestTime || configTime < earliestTime ? config : earliest
    }, null)
    const earliestTime = parsePenRecordTime(earliestConfig && (earliestConfig.createTime || earliestConfig.time))
    if (earliestConfig && earliestTime > parsePenRecordTime(INSULIN_CONFIG_BASELINE_TIME)) {
      return [
        {
          ...createInsulinConfigSnapshot(earliestConfig),
          createTime: INSULIN_CONFIG_BASELINE_TIME,
          configInferred: true
        },
        ...history
      ]
    }
    return history
  }

  const setting = parsePenSetting(penInfo.peizhi)
  if (!setting.insType) return history
  const ageMode = setting.insulinAgeMode || DEFAULT_INSULIN_AGE_MODE
  const activeTime = setting.activeTime || setting.activTime || penInfo.activTime
  const presetConfig = createInsulinConfigSnapshot({ insulinType: setting.insType, ageMode })
  const configuredBaseline = createInsulinConfigSnapshot({
    insulinType: setting.insType,
    ageMode,
    activeTime
  })
  const baselineConfig = {
    ...configuredBaseline,
    customTime:
      typeof setting.customTime === 'boolean'
        ? setting.customTime
        : configuredBaseline.activeTime !== presetConfig.activeTime
  }

  return [
    {
      createTime: INSULIN_CONFIG_BASELINE_TIME,
      ...baselineConfig,
      configInferred: true
    }
  ]
}

export function findInsulinConfigAt(history, injectionTime) {
  const injectionTimestamp = parsePenRecordTime(injectionTime)
  if (!injectionTimestamp) return null

  return parseInsulinConfigHistory(history).reduce((matched, config) => {
    if (!hasInsulinConfigSnapshot(config)) return matched
    const configTimestamp = parsePenRecordTime(config.createTime || config.time)
    if (!configTimestamp || configTimestamp > injectionTimestamp) return matched
    if (!matched) return config
    const matchedTimestamp = parsePenRecordTime(matched.createTime || matched.time)
    return configTimestamp >= matchedTimestamp ? config : matched
  }, null)
}

export function hasInsulinConfigSnapshot(record) {
  if (!record || typeof record.customTime !== 'boolean') return false
  const normalized = createInsulinConfigSnapshot(record)
  return (
    String(record.insulinType) === String(normalized.insulinType) &&
    record.ageMode === normalized.ageMode &&
    Number(record.activeTime) === normalized.activeTime &&
    Number(record.peakTime) === normalized.peakTime &&
    Number(record.delayTime) === normalized.delayTime
  )
}

export function attachInsulinConfigToRecords(records, history) {
  return mergePenRecords(records).map(record => {
    if (hasInsulinConfigSnapshot(record)) return record
    const config = findInsulinConfigAt(history, record.createTime)
    if (!hasInsulinConfigSnapshot(config)) return record

    return {
      ...record,
      insulinType: String(config.insulinType),
      ageMode: config.ageMode,
      activeTime: Number(config.activeTime),
      peakTime: Number(config.peakTime),
      delayTime: Number(config.delayTime),
      customTime: Boolean(config.customTime)
    }
  })
}

export function parseConfiguredPenRecords(records, configHistory) {
  return attachInsulinConfigToRecords(parsePenRecords(records), configHistory)
}

export function parsePenInfoRecords(penInfo = {}) {
  // 读取只负责解析，不给历史记录补写配置。配置快照只在新记录首次同步时生成。
  return mergePenRecords(parsePenRecords(penInfo.jilu))
}

function getPenRecordKey(record) {
  const recordTime = record && record.createTime
  const dose = Number(record && record.realInsulin)
  if (!recordTime || !parsePenRecordTime(recordTime) || !Number.isFinite(dose) || dose <= 0) return ''
  return `${recordTime}_${dose}`
}

export function mergePenRecords(...recordLists) {
  const recordMap = new Map()
  recordLists.forEach(records => {
    if (!Array.isArray(records)) return
    records.forEach(item => {
      const recordTime = item && item.createTime
      const dose = Number(item && item.realInsulin)
      if (!recordTime || !parsePenRecordTime(recordTime) || !Number.isFinite(dose) || dose <= 0) return
      const key = getPenRecordKey(item)
      const existingRecord = recordMap.get(key)
      if (hasInsulinConfigSnapshot(existingRecord) && !hasInsulinConfigSnapshot(item)) return
      const jiexiTime = (existingRecord && existingRecord.jiexiTime) || item.jiexiTime
      recordMap.set(key, {
        ...item,
        realInsulin: dose,
        ...(jiexiTime ? { jiexiTime } : {})
      })
    })
  })
  return Array.from(recordMap.values()).sort(
    (a, b) => parsePenRecordTime(b.createTime) - parsePenRecordTime(a.createTime)
  )
}

export function getLatestPenRecordTime(records) {
  return mergePenRecords(records).reduce((latestTime, item) => {
    return Math.max(latestTime, parsePenRecordTime(item.createTime))
  }, 0)
}

async function loadPenRecordContext(uid) {
  if (!uid) return { records: [], configHistory: [] }
  const { data } = await getPenInfo({ uid })
  if (data && data.code != null && String(data.code) !== '200') {
    throw new Error(data.msg || '胰岛素记录加载失败')
  }
  const penInfo = data && data.data && data.data[0] ? data.data[0] : {}
  const configHistory = getPenInfoConfigHistory(penInfo)
  return {
    records: mergePenRecords(parsePenRecords(penInfo.jilu)),
    configHistory
  }
}

export async function loadPenRecords(uid) {
  if (!uid) return []
  const context = await loadPenRecordContext(uid)
  return context.records
}

export function savePenRecords(uid, records, options = {}) {
  const incomingRecords = mergePenRecords(records)
  if (!uid) return Promise.resolve(incomingRecords)

  const saveTask = saveQueue.catch(() => {}).then(async () => {
    // 每次写入前重新读取接口，防止多个同步入口用旧数组互相覆盖。
    const { records: cloudRecords, configHistory } = await loadPenRecordContext(uid)
    const cloudRecordKeys = new Set(cloudRecords.map(getPenRecordKey).filter(Boolean))
    const incomingMap = new Map(incomingRecords.map(record => [getPenRecordKey(record), record]))
    const enrichedCloudRecords = cloudRecords.map(record => {
      const incoming = incomingMap.get(getPenRecordKey(record))
      if (!incoming) return record
      return {
        ...record,
        requestHex: record.requestHex || incoming.requestHex,
        responseHex: record.responseHex || incoming.responseHex,
        ...(record.jiexiTime || incoming.jiexiTime
          ? { jiexiTime: record.jiexiTime || incoming.jiexiTime }
          : {})
      }
    })
    const newIncomingRecords = incomingRecords.filter(
      record => !cloudRecordKeys.has(getPenRecordKey(record))
    )
    const configuredNewRecords = attachInsulinConfigToRecords(newIncomingRecords, configHistory)
    // 云端已有记录只补充原始指令；只有本次首次出现的记录才固化当时的配置快照。
    const mergedRecords = mergePenRecords(enrichedCloudRecords, configuredNewRecords)
    const { data } = await updatePenInfo({
      uid,
      jilu: JSON.stringify(mergedRecords)
    })
    if (data && String(data.code) !== '200') {
      throw new Error(data.msg || '胰岛素记录同步失败')
    }
    emitPenRecordsUpdated(uid, mergedRecords, options.penType)
    return mergedRecords
  })

  saveQueue = saveTask.catch(() => {})
  return saveTask
}
